

export interface TemplateFormState {
  templateName: string;
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  cin: string;
  gst: string;

  headerColor: string;
  footerColor: string;

  showHeaderBg: boolean;
  logoFile: File | null;
  logoUrl: string;

  logoAlignment: "left" | "center" | "right";
  textAlignment: "left" | "center" | "right";
  headerLayout: "stacked" | "logo-left" | "logo-right" | string;
  logoSize: "small" | "medium" | "large";
  showCompanyInfo: boolean;

  showCompanyFields: {
    name: boolean;
    email: boolean;
    phone: boolean;
    address: boolean;
  };

  //  Footer Visibility Controls
  showCompanyInfoFooter: boolean;
  showFooterFields: { [key: string]: boolean };
  footerText: string;
  enableQR: boolean;


  iqCode: string;
  issuedBy: string;
  issuedTo: string;
  subject: string;

  decorativeLines: number;
  lineWidth: "full" | "half";
  lineColor: string;
  customFields?: Array<{ id: string; key: string; value: string }>;
  headerFieldOrder?: string[] | Record<string, unknown>;
  footerFieldOrder?: string[] | Record<string, unknown>;
}

export interface TemplateComponent {
  type: "header" | "body" | "footer";
  displayOrder: number;

  properties: {
    backgroundColor?: string;
    showBackground?: boolean;
    showCompanyInfo?: boolean;
    headerLayout?: string;
    textAlignment?: "left" | "center" | "right" | string;

    logoAlignment?: "left" | "center" | "right";
    logoUrl?: string;
    logoSize?: "small" | "medium" | "large";
    visibleFields?: {
      name?: boolean;
      email?: boolean;
      phone?: boolean;
      address?: boolean;
    };
    decorativeLines?: number;
    lineWidth?: "full" | "half";
    lineColor?: string;
    text?: string;
    enableQR?: boolean;
    qrData?: {
      iqCode?: string;
      issuedBy?: string;
      issuedTo?: string;
      subject?: string;
    };
  };

  identityFields?: Array<{
    key: string;
    value: string;
  }>;
  headerFieldOrder?: string | string[] | Record<string, unknown>;
  footerFieldOrder?: string | string[] | Record<string, unknown>;
}


export interface CreateTemplatePayload {
  company_id: string;
  templateName: string;
  status: "active" | "inactive";
  components: TemplateComponent[];
}


export interface LetterItem {
  letter_id: number;
  template_id: number;
  company_id: string;
  subject: string;
  body: string; 
  status: "DRAFT" | "SENT" | "SIGNED" | "CANCELLED";
  created_at: string;
  updated_at: string;

  letter_recipients: {
    recipient_id: number;
    letter_id: number;
    name: string;
    email: string;
    address: string;
  }[];

  signature_setting: {
    signature_id: number;
    letter_id: number;
    signature_type: "draw" | "upload" | "digital";
    verification_status: "pending" | "verified" | "rejected";
    verification_provider: string | null;
    verification_reference: string | null;
    created_at: string;
    updated_at: string;

    signature_files: {
      file_id: number;
      signature_id: number;
      file_name: string;
      file_type: string;
      file_size: number;
      file_path: string;
      created_at: string;
    }[];
  } | null;
}

export interface TemplateItem {
  template_id: number;
  company_id: string;
  template_name: string;
  status: "active" | "inactive";
  created_at: string;

  TemplateComponents: {
    component_id: number;
    component_type: "header" | "body" | "footer";
    display_order: number;
    template_id: number;

    TemplateProperties: {
      property_id: number;
      property_key: string;
      property_value: string; // API string me de raha hai (even JSON & boolean)
      value_type: string;
      component_id: number;
    }[];
  }[];

  TemplateIdentities: {
    identity_field_id: number;
    field_key: string;
    field_value: string;
    position: "header" | "body" | "footer";
    display_order: number;
    template_id: number;
  }[];
}




// --- LETTER TYPES ---
export interface CreateLetterPayload {
  template_id: number;
  letter_title: string;
  letter_subject: string;
  letter_content: string;

  recipient_name?: string;
  recipient_email?: string;
  recipient_address?: string;

  status: "DRAFT" | "SENT" | "SIGNED" | "CANCELLED";

  meta_data?: string;
}
export interface LetterFormState {
  templateId: string | number;
  recipientName: string;
  recipientEmail: string;
  recipientAddress: string;
  subject: string;
  body: string;
  status: string;
  signatureType: string;
  signaturePlacement: string;
  signaturePagePoint: string | number;
  signatories: Array<{ id: string; role: string; name: string; email: string; status: string }>;
  drawnSignature: any;
  uploadedSignatureFile: any;
  uploadedSignatureUrl: any;
  enableQR: boolean;
  iqCode: string;
  issuedBy: string;
  issuedTo: string;
  subjectQr: string;
  esignVerified: boolean;
  esignReference: any;
  esignSignerName: any;
  esignDate: any;
  esignProvider: any;
  signatoryName: string;
  signatoryEmail: string;
  created_at?: string;
  sent_at?: string | null;
}

export const generateShortId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 10; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
  return result;
};

export const INITIAL_TEMPLATE_STATE: TemplateFormState = {
  templateName: "", companyName: "", companyEmail: "", companyPhone: "", companyAddress: "",
  cin: "", gst: "", headerColor: "", footerColor: "", showHeaderBg: true, logoFile: null,
  logoUrl: "", logoSize: "medium", showCompanyInfo: true, headerLayout: "stacked",
  logoAlignment: "left", textAlignment: "left",
  showCompanyFields: { name: true, email: true, phone: true, address: true },
  showCompanyInfoFooter: true,
  showFooterFields: { name: false, email: false, phone: false, address: false, cin: true, gst: true },
  footerText: "", enableQR: false, iqCode: "", issuedBy: "", issuedTo: "", subject: "",
  decorativeLines: 1, lineWidth: "full", lineColor: "#6B7280", customFields: [],
  headerFieldOrder: undefined,
  footerFieldOrder: undefined,
};

// export const INITIAL_LETTER_STATE: LetterFormState = {
//   templateId: "", recipientName: "", recipientEmail: "", recipientAddress: "", subject: "",
//   body: "", status: "DRAFT", signatureType: "text", signaturePlacement: "last_page",
//   signaturePagePoint: "",
//   signatories: [{ id: generateShortId(), role: 'HR', name: '', email: '', status: 'PENDING' }],
//   signatureType: 'send_link',
//   drawnSignature: null, uploadedSignatureFile: null, uploadedSignatureUrl: null,
//   enableQR: false, iqCode: "", issuedBy: "", issuedTo: "", subjectQr: "",
//   esignVerified: false, esignReference: null, esignSignerName: null, esignDate: null,
//   esignProvider: null, signatoryName: "", signatoryEmail: "",
// };


// src/pages/letterhead/types/letterhead.types.ts

export const INITIAL_LETTER_STATE: LetterFormState = {
  templateId: '',
  recipientName: '',
  recipientEmail: '',
  recipientAddress: '',
  subject: '',
  body: '',
  status: 'DRAFT',
  
  // Signature related
  signatureType: 'send_link',
  signaturePlacement: 'last_page',
  signaturePagePoint: '',
  signatories: [
    { 
      id: generateShortId(), 
      role: 'Signatory', 
      name: '', 
      email: '', 
      status: 'PENDING' 
    }
  ],
  uploadedSignatureUrl: '',
  uploadedSignatureFile: null,
  drawnSignature: '',
  esignVerified: false,
  esignSignerName: '',
  esignReference: '',
  esignProvider: '',
  esignDate: '',
  
  // QR related
  iqCode: '',
  enableQR: false,
  issuedBy: '',
  issuedTo: '',
  subjectQr: '',
  
  // ... other properties
};